import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../redux/authSlice';
import { RootState } from '../redux/store';
import { UserRole } from '../redux/authSlice';
import RoleGuard from './RoleGuard';
import { Menu, X, LogOut, User, LayoutDashboard, ShieldCheck } from 'lucide-react';

interface NavbarProps {
  onLoginClick?: () => void;
  onGetStartedClick?: () => void;
}

interface NavItem {
  path: string;
  label: string;
  requiredRole?: UserRole;
}

const Navbar: React.FC<NavbarProps> = ({ onLoginClick, onGetStartedClick }) => {
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    dispatch(logout());
    setIsMobileMenuOpen(false);
  };

  const navItems: NavItem[] = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/ohmyfin', label: 'OhMyFinAI' },
    { path: '/contact', label: 'Contact' },
    { path: '/dashboard/home', label: 'Dashboard', requiredRole: 'user' },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
          isScrolled
            ? 'bg-white/84 backdrop-blur-xl border-b border-white/60 shadow-[0_10px_30px_rgba(9,25,49,0.14)]'
            : 'bg-white/64 backdrop-blur-md border-b border-white/40'
        }`}
      >
        <div className="section-container">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center gap-2.5 text-fintech-primary font-bold text-xl no-underline hover:text-fintech-primary"
            >
              <div className="w-8 h-8 bg-fintech-primary rounded-lg flex items-center justify-center shadow-sm">
                <span className="text-white text-sm font-bold">K</span>
              </div>
              <span className="hidden sm:block tracking-tight">Kenluk Pay</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-0.5">
              {navItems.map((item) => (
                <div key={item.path}>
                  {item.requiredRole ? (
                    <RoleGuard requiredRole={item.requiredRole}>
                      <Link
                        to={item.path}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 no-underline ${
                          isActive(item.path)
                            ? 'bg-blue-50 text-fintech-primary'
                            : 'text-fintech-textMuted hover:text-fintech-text hover:bg-slate-50'
                        }`}
                      >
                        <LayoutDashboard size={14} />
                        {item.label}
                      </Link>
                    </RoleGuard>
                  ) : (
                    <Link
                      to={item.path}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 no-underline ${
                        isActive(item.path)
                          ? 'bg-blue-50 text-fintech-primary'
                          : 'text-fintech-textMuted hover:text-fintech-text hover:bg-slate-50'
                      }`}
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}
            </div>

            {/* Desktop Auth Actions */}
            <div className="hidden md:flex items-center gap-2.5">
              {isAuthenticated ? (
                <>
                  {/* User info chip */}
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-50 border border-fintech-border">
                    <div className="w-6 h-6 rounded-full bg-fintech-primary flex items-center justify-center">
                      {user?.role === 'admin' ? (
                        <ShieldCheck size={11} className="text-white" />
                      ) : (
                        <User size={11} className="text-white" />
                      )}
                    </div>
                    <div>
                      <span className="text-sm font-medium text-fintech-text leading-none">{user?.name}</span>
                      {user?.role === 'admin' && (
                        <span className="block text-xs text-amber-600 font-medium leading-none mt-0.5">Admin</span>
                      )}
                    </div>
                  </div>
                  <button
                    className="btn-ghost text-fintech-textMuted hover:text-fintech-error hover:bg-red-50"
                    onClick={handleLogout}
                    aria-label="Log out"
                  >
                    <LogOut size={15} />
                    <span>Log Out</span>
                  </button>
                </>
              ) : (
                <>
                  <button
                    className="btn-secondary"
                    onClick={() => { onLoginClick?.(); }}
                    aria-label="Log in"
                  >
                    Log In
                  </button>
                  <button
                    className="btn-primary"
                    onClick={() => { onGetStartedClick?.(); }}
                    aria-label="Get started"
                  >
                    Get Started
                  </button>
                </>
              )}
            </div>

            {/* Mobile Menu Toggle */}
            <button
              className="md:hidden p-2 rounded-lg text-fintech-textMuted hover:bg-slate-100 transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle mobile menu"
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Spacer for fixed navbar */}
      <div className="h-16" />

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-30 md:hidden" onClick={() => setIsMobileMenuOpen(false)}>
          <div className="absolute inset-0 bg-fintech-text/20 backdrop-blur-sm" />
          <div
            className="absolute top-16 left-0 right-0 bg-white/90 backdrop-blur-xl border-b border-white/50 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="section-container py-4">
              {/* Nav Links */}
              <nav className="flex flex-col gap-0.5 mb-4">
                {navItems.map((item) => (
                  <div key={item.path}>
                    {item.requiredRole ? (
                      <RoleGuard requiredRole={item.requiredRole}>
                        <Link
                          to={item.path}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors no-underline ${
                            isActive(item.path)
                              ? 'bg-blue-50 text-fintech-primary'
                              : 'text-fintech-text hover:bg-slate-50'
                          }`}
                        >
                          <LayoutDashboard size={16} />
                          {item.label}
                        </Link>
                      </RoleGuard>
                    ) : (
                      <Link
                        to={item.path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-colors no-underline ${
                          isActive(item.path)
                            ? 'bg-blue-50 text-fintech-primary'
                            : 'text-fintech-text hover:bg-slate-50'
                        }`}
                      >
                        {item.label}
                      </Link>
                    )}
                  </div>
                ))}
              </nav>

              {/* Mobile Auth */}
              <div className="border-t border-fintech-border pt-4">
                {isAuthenticated ? (
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 rounded-xl border border-fintech-border">
                      <div className="w-9 h-9 rounded-full bg-fintech-primary flex items-center justify-center flex-shrink-0">
                        {user?.role === 'admin' ? (
                          <ShieldCheck size={15} className="text-white" />
                        ) : (
                          <User size={15} className="text-white" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-fintech-text">{user?.name}</p>
                        <p className="text-xs text-fintech-textMuted capitalize">{user?.role}</p>
                      </div>
                    </div>
                    <button
                      className="btn-ghost w-full justify-center text-fintech-error hover:bg-red-50"
                      onClick={handleLogout}
                    >
                      <LogOut size={16} />
                      Log Out
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    <button
                      className="btn-secondary w-full justify-center"
                      onClick={() => { onLoginClick?.(); setIsMobileMenuOpen(false); }}
                    >
                      Log In
                    </button>
                    <button
                      className="btn-primary w-full justify-center"
                      onClick={() => { onGetStartedClick?.(); setIsMobileMenuOpen(false); }}
                    >
                      Get Started
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
